import { Box, Button, CircularProgress, Switch, Typography } from "@mui/material";
import { PinnacleTextField } from "../../../../components/PinnacleTextField";
import { E_FIELD_REQUIRED } from "../../../../utils/constants";
import { FormData } from "../../hooks/useExamCodeForm";

interface EditExamCodeDrawerContentProps {
    formData: FormData;
    touchedFields: Record<keyof FormData, boolean>;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur: (field: keyof FormData) => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    isFormValid: () => boolean;
    isFormModified: boolean;
}

const EditExamCodeDrawerContent: React.FC<EditExamCodeDrawerContentProps> = ({
    formData,
    touchedFields,
    onChange,
    onBlur,
    onSubmit,
    isSubmitting,
    isFormValid,
    isFormModified
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <PinnacleTextField
                label="Examination Name"
                name="name"
                value={formData.name}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('name')}
                error={touchedFields.name && !formData.name.trim()}
                helperText={
                    touchedFields.name && !formData.name.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <PinnacleTextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={onChange}
                multiline
                rows={5}
                fullWidth
                onBlur={() => onBlur('description')}
                error={touchedFields.description && !formData.description.trim()}
                helperText={
                    touchedFields.description && !formData.description.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <PinnacleTextField
                label="Examination Code"
                name="code"
                value={formData.code}
                onChange={onChange}
                fullWidth
                onBlur={() => onBlur('code')}
                error={touchedFields.code && !formData.code.trim()}
                helperText={
                    touchedFields.code && !formData.code.trim()
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />


            <PinnacleTextField
                label="Start Date"
                type="datetime-local"
                name="startDate"
                value={formData.startDate || ''}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                onBlur={() => onBlur('startDate')}
                error={touchedFields.startDate && !formData.startDate}
                helperText={
                    touchedFields.startDate && !formData.startDate
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />


            <PinnacleTextField
                label="End Date"
                type="datetime-local"
                name="endDate"
                value={formData.endDate || ''}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                onBlur={() => onBlur('endDate')}
                error={touchedFields.endDate && !formData.endDate}
                helperText={
                    touchedFields.endDate && !formData.endDate
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <PinnacleTextField
                label="Show Answers Date"
                type="datetime-local"
                name="showAnswersDate"
                value={formData.showAnswersDate || ''}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                onBlur={() => onBlur('showAnswersDate')}
                error={touchedFields.showAnswersDate && !formData.showAnswersDate}
                helperText={
                    touchedFields.showAnswersDate && !formData.showAnswersDate
                        ? E_FIELD_REQUIRED
                        : ' '
                }
                disabled={isSubmitting}
            />

            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", mb: 3 }}
            >
                <Typography variant="body2" sx={{ color: '#818181' }}>
                    Set this exam as <Box component="span" sx={{ fontWeight: 'bold' }}>“EXCLUSIVE”</Box>?
                </Typography>

                <Switch
                    checked={formData.isExclusive}
                    onChange={onChange}
                    disabled={isSubmitting}
                    name="isExclusive"
                />
            </Box>

            <Button
                variant="contained"
                color="primary"
                onClick={onSubmit}
                disabled={isSubmitting || !isFormValid() || !isFormModified}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            >
                SAVE CHANGES
            </Button>
        </Box>
    );
};

export default EditExamCodeDrawerContent;